import debounce from 'lodash/fp/debounce';
import { useCallback, useEffect, useRef, useState, ChangeEvent } from 'react';

import { ColorPickerFieldProps } from '.';

const HASH = '#';
const HEX_COLOR_REGEXP = /^#([A-Fa-f0-9]{8}|[A-Fa-f0-9]{6}|([A-Fa-f0-9]{6}[\d]{2}))$/i;

export const useColorPickerField = ({
  initialColor,
  onColorChange
}: Pick<ColorPickerFieldProps, 'initialColor' | 'onColorChange'>) => {
  const [color, setColor] = useState(initialColor);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setColor(initialColor);
  }, [initialColor]);

  const handleFocus = useCallback(() => inputRef.current?.select(), []);

  const handleBlur = useCallback(() => {
    const isValidLength = HEX_COLOR_REGEXP.test(inputRef.current?.defaultValue!);

    isValidLength ? onColorChange(color) : setColor(initialColor);
  }, [color, inputRef]);

  const handleChangeColor = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => {
      const val = target.value;
      const currentVal = val.includes(HASH) ? val : `${HASH}${val}`;
      const hexVal = currentVal.slice(0, 9);
      setColor(hexVal);
    },
    [color]
  );

  const handleColorPickerChange = debounce(50, (color: string) => onColorChange(color));

  return [color, inputRef, handleBlur, handleFocus, handleChangeColor, handleColorPickerChange] as const;
};
